// src/client/ui/screens/character-preview-adapter.ts
//
// Thin adapter façade the Character Select screen consumes instead of
// depending on Plan 19's asset-loading pre-cache directly (Plan 24a, Task 1.2).
//
// In production, Plan 19 supplies a real adapter wrapping `createPreviewRig`
// from `src/client/renderer/character`. In tests and headless screen mounts,
// a stub adapter is passed instead.

import * as THREE from 'three';
import type {
  CharacterCustomization,
  CharacterPreviewRig,
  ColorId,
  HeadId,
  EyeId,
  CapeId,
} from '../../renderer/character';
import { createPreviewRig } from '../../renderer/character/preview-rig';
import type { CharacterAnchors } from '../../renderer/character/preview-rig';
import type { EyeTextureStore } from '../../renderer/character/eye-texture-store';
import { addCharacterAnimationClips } from '../../renderer/character';
import { ALL_CAPE_IDS, ALL_COLOR_IDS, ALL_EYE_IDS, ALL_HEAD_IDS } from '@shared/game/types';

/** Adapter the Character Select screen consumes instead of depending on
 *  Plan 19's loaded GLB/texture pre-cache directly. */
export interface CharacterPreviewAdapter {
  /** Build a preview rig with the given customization. */
  buildRig(initial: CharacterCustomization): CharacterPreviewRig;
  /** Merge optional animation bundles into future and currently-live rigs. */
  addAnimationClips?(clips: readonly THREE.AnimationClip[]): void;
  /** Lists which color/head/eye/cape ids are loaded + selectable RIGHT NOW. */
  inventory(): {
    colors: readonly ColorId[];
    heads: readonly HeadId[];
    eyes: readonly EyeId[];
    capes: readonly CapeId[];
  };
}

/** Stub adapter for tests + headless screen mounts. */
export interface StubAdapterOpts {
  inventory?: {
    colors?: readonly ColorId[];
    heads?: readonly HeadId[];
    eyes?: readonly EyeId[];
    capes?: readonly CapeId[];
  };
}

export interface StubAdapterHandle {
  readonly adapter: CharacterPreviewAdapter;
  /** Customizations passed to the most recently built rig's applyCustomization. */
  readonly customizationsApplied: readonly CharacterCustomization[];
}

// ── Default roster constants (mirror the type unions from types.ts) ───────────

const DEFAULT_COLORS: readonly ColorId[] = ALL_COLOR_IDS;
const DEFAULT_HEADS: readonly HeadId[] = ALL_HEAD_IDS;
const DEFAULT_EYES: readonly EyeId[] = ALL_EYE_IDS;
/** 9 capes — 'none' is first (spec §9 line 669: default, no cape mesh). */
const DEFAULT_CAPES: readonly CapeId[] = ALL_CAPE_IDS;

// ── Stub factory ──────────────────────────────────────────────────────────────

/**
 * Creates a stub `CharacterPreviewAdapter` suitable for tests and headless
 * screen mounts. Uses `THREE.Group` / `THREE.Mesh` / `THREE.Vector3` only
 * (no canvas / WebGLRenderer required, safe with happy-dom).
 *
 * All customizations applied across ALL rigs built by one adapter instance
 * accumulate in the shared `customizationsApplied` array.
 */
export function createStubAdapter(opts?: StubAdapterOpts): StubAdapterHandle {
  // Shared accumulator — all rigs built by this adapter push into this array.
  const customizationsApplied: CharacterCustomization[] = [];

  const resolvedInventory = {
    colors: opts?.inventory?.colors ?? DEFAULT_COLORS,
    heads: opts?.inventory?.heads ?? DEFAULT_HEADS,
    eyes: opts?.inventory?.eyes ?? DEFAULT_EYES,
    capes: opts?.inventory?.capes ?? DEFAULT_CAPES,
  };

  const adapter: CharacterPreviewAdapter = {
    inventory() {
      return resolvedInventory;
    },

    buildRig(_initial: CharacterCustomization): CharacterPreviewRig {
      const root = new THREE.Group();
      const visualRoot = new THREE.Group();
      const modelRoot = new THREE.Group();
      const bodyMesh = new THREE.Mesh();
      visualRoot.add(modelRoot);
      modelRoot.add(bodyMesh);
      root.add(visualRoot);

      const anchors: CharacterAnchors = {
        nameplate: new THREE.Vector3(),
        speechBubble: new THREE.Vector3(),
      };

      function applyCustomization(c: CharacterCustomization): void {
        customizationsApplied.push(c);
      }

      function setEyeExpression(): void {
        // no-op — stub has no eye meshes
      }

      function dispose(): void {
        // no-op — stub holds no GPU resources
      }

      const rig: CharacterPreviewRig = {
        root,
        visualRoot,
        modelRoot,
        bodyMesh,
        bodySkinnedMesh: null,
        renderMeshes: [bodyMesh],
        colorableMeshes: [bodyMesh],
        mixer: null,
        actions: new Map(),
        tick: () => {},
        playAnimation: (alias: string) => alias === 'idle',
        resetSimulation: () => {},
        resolveSocket: () => null,
        anchors,
        applyCustomization,
        setEyeExpression,
        dispose,
        // Internal slot trackers — null in the stub (no real meshes attached)
        _headRoot: null,
        _headMesh: null,
        _eyeSprite: null,
        _eyeGroup: null,
        _eyeGeneration: 0,
        _eyeExpression: 'base',
        _capeMesh: null,
      };

      return rig;
    },
  };

  return {
    adapter,
    get customizationsApplied(): readonly CharacterCustomization[] {
      return customizationsApplied;
    },
  };
}

// ── Production adapter ────────────────────────────────────────────────────────

/** Options for `createProductionAdapter`. All GLB scenes + textures are pre-loaded
 *  by the boot-time preload in `main.ts` before this factory is called. */
export interface ProductionAdapterOpts {
  bodyGltfScene: THREE.Group;
  bodyAnimations?: readonly THREE.AnimationClip[];
  headGltfScenes: Partial<Record<HeadId, THREE.Group>>;
  fallbackHeadGltfScene: THREE.Group;
  manifestEntries?: Partial<Record<HeadId, { placeholder: boolean }>>;
  eyeTextures: Partial<Record<EyeId, THREE.Texture>>;
  eyeTextureStore?: EyeTextureStore;
  capeTextures: Partial<Record<Exclude<CapeId, 'none'>, THREE.Texture>>;
  inventory?: {
    colors?: readonly ColorId[];
    heads?: readonly HeadId[];
    eyes?: readonly EyeId[];
    capes?: readonly CapeId[];
  };
}

/**
 * Creates a production `CharacterPreviewAdapter` backed by pre-loaded
 * `THREE.Group` GLB scenes and `THREE.Texture` instances. Cosmetic maps may be
 * hydrated after boot; `inventory()` reflects the assets available right now.
 *
 * `buildRig(initial)` delegates to `createPreviewRig` which internally
 * calls `assembleCharacter` — that clones the body GLB, so reusing the
 * same `bodyGltfScene` reference across multiple `buildRig` calls is safe.
 *
 * Optional `inventory` overrides still constrain the visible rosters.
 */
export function createProductionAdapter(opts: ProductionAdapterOpts): CharacterPreviewAdapter {
  const animationClips = [...(opts.bodyAnimations ?? [])];
  const liveRigs = new Set<CharacterPreviewRig>();
  const inventoryLimits = {
    colors: opts.inventory?.colors ?? DEFAULT_COLORS,
    heads: opts.inventory?.heads ?? DEFAULT_HEADS,
    eyes: opts.inventory?.eyes ?? DEFAULT_EYES,
    capes: opts.inventory?.capes ?? DEFAULT_CAPES,
  };

  return {
    inventory() {
      return {
        colors: inventoryLimits.colors,
        heads: inventoryLimits.heads.filter((headId) => opts.headGltfScenes[headId] != null),
        eyes: inventoryLimits.eyes.filter((eyeId) => opts.eyeTextures[eyeId] != null),
        capes: inventoryLimits.capes.filter(
          (capeId) => capeId === 'none' || opts.capeTextures[capeId] != null,
        ),
      };
    },

    buildRig(initial: CharacterCustomization): CharacterPreviewRig {
      const rig = createPreviewRig({
        bodyGltfScene: opts.bodyGltfScene,
        bodyAnimations: animationClips,
        headGltfScenes: opts.headGltfScenes,
        fallbackHeadGltfScene: opts.fallbackHeadGltfScene,
        manifestEntries: opts.manifestEntries,
        eyeTextures: opts.eyeTextures,
        eyeTextureStore: opts.eyeTextureStore,
        capeTextures: opts.capeTextures,
        initial,
      });
      liveRigs.add(rig);
      const dispose = rig.dispose;
      rig.dispose = () => {
        liveRigs.delete(rig);
        dispose();
      };
      return rig;
    },

    addAnimationClips(clips: readonly THREE.AnimationClip[]): void {
      if (clips.length === 0) return;
      animationClips.push(...clips);
      for (const rig of liveRigs) {
        addCharacterAnimationClips(rig, clips);
      }
    },
  };
}
